import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { saveQuiz } from "@/lib/api";

// Generic classification step: for each item, pick one label. Handles anx/dep and attributions.
export default function ClassifyStep({ id, title, intro, items, options, labels, sessionNumber, onDone }) {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const choices = options || labels; // labels: [{key,label}]

  const allAnswered = Object.keys(answers).length === items.length;

  const save = async () => {
    if (!allAnswered) return;
    setSaving(true);
    try {
      const detailed = items.map((it, idx) => ({
        question_id: `${id}-${idx}`,
        selected: answers[idx],
        correct: answers[idx] === it.correct,
      }));
      const score = detailed.filter(a => a.correct).length;
      await saveQuiz(id, detailed, score);
      toast.success(`Puntuación: ${score}/${items.length}`);
      onDone({ score });
    } catch {
      toast.error("No pudimos guardar el ejercicio.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {intro && <p className="text-[var(--ha-muted)] leading-relaxed max-w-2xl">{intro}</p>}
      <div className="mt-8 space-y-4">
        {items.map((it, idx) => {
          const chosen = answers[idx];
          return (
            <Card key={idx} className="rounded-2xl border-[var(--ha-border)] bg-white">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Situación {idx + 1}</p>
                <p className="mt-2 font-semibold text-[var(--ha-text)] leading-snug">{it.text}</p>
                <div className={`mt-4 grid gap-2 grid-cols-1 sm:grid-cols-${Math.min(choices.length, 4)}`}>
                  {choices.map(opt => {
                    const key = opt.key;
                    const isSelected = chosen === key;
                    const isCorrect = key === it.correct;
                    const showFeedback = chosen !== undefined;
                    return (
                      <button
                        key={key}
                        data-testid={`${id}-${idx}-${key}`}
                        onClick={() => setAnswers({ ...answers, [idx]: key })}
                        className={`text-left rounded-xl border px-3 py-2.5 flex items-center gap-2 transition-colors ${
                          showFeedback && isSelected && isCorrect ? "border-[var(--ha-success)] bg-[var(--ha-success)]/10" :
                          showFeedback && isSelected && !isCorrect ? "border-[var(--ha-error)] bg-[var(--ha-error)]/10" :
                          "border-[var(--ha-border)] hover:bg-[var(--ha-bg)]"
                        }`}
                      >
                        <span className="text-sm text-[var(--ha-text)] flex-1">{opt.label}</span>
                        {showFeedback && isSelected && (isCorrect ? <CheckCircle2 className="w-4 h-4 text-[var(--ha-success)]" /> : <XCircle className="w-4 h-4 text-[var(--ha-error)]" />)}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="mt-8 flex items-center justify-between">
        <span className="text-sm text-[var(--ha-muted)]">{Object.keys(answers).length}/{items.length} respondidas</span>
        <Button data-testid={`${id}-continue-btn`} onClick={save} disabled={!allAnswered || saving} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
          Continuar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
